/**
 * useColorScheme — Global color-scheme preference
 *
 * Supports three user preferences:
 * - `system`: follow the OS/browser preference
 * - `dark`: force dark mode
 * - `light`: force light mode
 *
 * The resolved light/dark scheme is applied to `<html>` via the `dark` class
 * and `color-scheme`, while the user's preference is persisted in localStorage.
 */
import { useCallback, useEffect, useState } from 'react'

export type ResolvedColorScheme = 'dark' | 'light'
export type ColorSchemePreference = ResolvedColorScheme | 'system'

const STORAGE_KEY = 'phyre-color-scheme'
const LEGACY_EXPLICIT_STORAGE_KEY = 'phyre-color-scheme-explicit'
const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)'

function resolveSystemScheme(): ResolvedColorScheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light'
  return window.matchMedia(DARK_MEDIA_QUERY).matches ? 'dark' : 'light'
}

function getInitialPreference(): ColorSchemePreference {
  if (typeof window === 'undefined') return 'system'

  const stored = localStorage.getItem(STORAGE_KEY)
  const legacyExplicit = localStorage.getItem(LEGACY_EXPLICIT_STORAGE_KEY)

  if (stored === 'system') return 'system'
  if (stored === 'dark' || stored === 'light') {
    // Migrate the legacy 2-state preference model:
    // explicit=0 meant "app fallback", which should now become system.
    if (legacyExplicit === '0') return 'system'
    return stored
  }

  return 'system'
}

function applyScheme(scheme: ResolvedColorScheme) {
  const root = document.documentElement
  if (scheme === 'dark') {
    root.classList.add('dark')
    root.style.colorScheme = 'dark'
  } else {
    root.classList.remove('dark')
    root.style.colorScheme = 'light'
  }
}

export function useColorScheme() {
  const [preference, setPreference] = useState<ColorSchemePreference>(getInitialPreference)
  const [systemScheme, setSystemScheme] = useState<ResolvedColorScheme>(resolveSystemScheme)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return

    const mediaQuery = window.matchMedia(DARK_MEDIA_QUERY)
    const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
      setSystemScheme(event.matches ? 'dark' : 'light')
    }

    handleChange(mediaQuery)

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }

    mediaQuery.addListener(handleChange)
    return () => mediaQuery.removeListener(handleChange)
  }, [])

  const resolvedScheme: ResolvedColorScheme = preference === 'system' ? systemScheme : preference

  useEffect(() => {
    applyScheme(resolvedScheme)
    localStorage.setItem(STORAGE_KEY, preference)
    localStorage.removeItem(LEGACY_EXPLICIT_STORAGE_KEY)
  }, [preference, resolvedScheme])

  const setPreferenceExplicit = useCallback(
    (next: ColorSchemePreference | ((prev: ColorSchemePreference) => ColorSchemePreference)) => {
      setPreference(next)
    },
    [],
  )

  const toggle = useCallback(() => {
    setPreference((current) => {
      const activeScheme = current === 'system' ? systemScheme : current
      return activeScheme === 'dark' ? 'light' : 'dark'
    })
  }, [systemScheme])

  const setDark = useCallback(() => setPreferenceExplicit('dark'), [setPreferenceExplicit])
  const setLight = useCallback(() => setPreferenceExplicit('light'), [setPreferenceExplicit])
  const setSystem = useCallback(() => setPreferenceExplicit('system'), [setPreferenceExplicit])

  return {
    scheme: preference,
    preference,
    resolvedScheme,
    isDark: resolvedScheme === 'dark',
    isSystem: preference === 'system',
    toggle,
    setPreference: setPreferenceExplicit,
    setDark,
    setLight,
    setSystem,
  }
}
